/**
 * One turn in the conversation.
 *
 * The assistant's turns are plain typographic blocks rather than bubbles: they
 * are the questions, they carry the page, and boxing them would make the
 * intake read as a form again — which is the thing the chat replaced. The
 * SME's own answers are bubbles, right-aligned, so the two are never confused.
 *
 * ── The edit affordance ─────────────────────────────────────────────────────
 * Hovering an answer reveals a pencil. Editing offers two things, and the
 * default is the one people expect from a chat: **send again**, which discards
 * what came after and asks again from that point. The secondary action keeps
 * everything after it and lets the assistant pick the correction up on its next
 * turn. Both are stated in one short line each — an SME editing answer two of
 * eight deserves to know which one loses the other six.
 */

import { useEffect, useRef, useState } from 'react';
import { ds } from '../../ds';
import { EASE, RADIUS, mono } from '../../theme';
import type { ChatMessage } from '../../api';

interface Props {
  message: ChatMessage;
  index: number | null;
  editable: boolean;
  onRewind: (content: string) => void;
  onEditInPlace: (content: string) => void;
}

export function MessageBubble({ message, index, editable, onRewind, onEditInPlace }: Props) {
  const { Button, Icon } = ds();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  const box = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) box.current?.focus();
  }, [editing]);

  const start = () => {
    setDraft(message.content);
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  if (message.role === 'assistant') {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: index === null ? 'minmax(0,1fr)' : '34px minmax(0,1fr)',
          gap: 14,
          padding: '10px 18px 10px 0',
          animation: `mvIn .3s ${EASE}`
        }}
      >
        {index !== null && (
          <div style={{ ...mono, fontSize: 11, letterSpacing: '.1em', color: 'var(--text-quiet)', paddingTop: 4 }}>
            {String(index).padStart(2, '0')}
          </div>
        )}
        <div
          style={{
            minWidth: 0,
            fontSize: 16,
            lineHeight: 1.6,
            color: 'var(--text-body)',
            whiteSpace: 'pre-wrap',
            textWrap: 'pretty'
          }}
        >
          {message.content}
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 0' }}>
        <div style={{ width: 'min(560px, 100%)' }}>
          <textarea
            ref={box}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              resize: 'vertical',
              border: '1px solid var(--border-strong)',
              borderRadius: RADIUS.control,
              background: 'var(--surface-card)',
              padding: '12px 14px',
              fontSize: 15,
              lineHeight: 1.6,
              color: 'var(--text-body)',
              outline: 'none'
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <Button
              variant="primary"
              size="sm"
              disabled={!draft.trim()}
              onClick={() => {
                onRewind(draft.trim());
                cancel();
              }}
            >
              Send again
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!draft.trim()}
              onClick={() => {
                onEditInPlace(draft.trim());
                cancel();
              }}
            >
              Edit in place
            </Button>
            <Button variant="ghost" size="sm" onClick={cancel}>
              Cancel
            </Button>
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-faint)' }}>
            Send again discards your later answers. Edit in place keeps them.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mv-msg"
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        gap: 6,
        padding: '6px 0',
        animation: `mvIn .3s ${EASE}`
      }}
    >
      {editable && (
        <button
          className="mv-edit"
          type="button"
          aria-label="Edit this answer"
          onClick={start}
          style={{
            border: 0,
            background: 'transparent',
            borderRadius: RADIUS.chip,
            padding: 6,
            marginTop: 4,
            color: 'var(--text-quiet)',
            display: 'flex',
            transition: `opacity 120ms ${EASE}`
          }}
        >
          <Icon name="pencil" size={15} />
        </button>
      )}
      <div
        style={{
          maxWidth: 'min(560px, 88%)',
          background: 'var(--surface-inset)',
          borderRadius: RADIUS.card,
          padding: '12px 16px',
          fontSize: 15,
          lineHeight: 1.6,
          color: 'var(--text-body)',
          whiteSpace: 'pre-wrap',
          textWrap: 'pretty'
        }}
      >
        {message.content}
        {message.editedAt !== null && (
          <span style={{ ...mono, fontSize: 9, marginLeft: 8, color: 'var(--text-quiet)' }}>Edited</span>
        )}
      </div>
    </div>
  );
}
